import { Component, OnInit, Input, OnChanges, OnDestroy, HostListener } from '@angular/core';
import { AbstractControl, FormGroup, FormControl, Validators } from '@angular/forms';
import { DateUtil } from '../../../utils/date.util';
import * as moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { FloatLabelType } from '@angular/material/form-field';

/**
 * DOB Component
 */
@Component({
	selector: 'app-dob',
	templateUrl: './dob.component.html',
	styleUrls: ['./dob.component.scss'],
})
export class DobComponent implements OnInit, OnChanges, OnDestroy {
	isMobileView: boolean = false;
	/** gets form group in which dob has to be set as input */
	@Input() formGroup: FormGroup;
	/** gets formControl if given */
	@Input() control?: string;
	/** optional mat-label to add */
	@Input() fieldLabel?: string;
	/** if only one mat-label */
	@Input() labelOnEveryField?: boolean;
	/** optional styling to add */
	@Input() floatLabel?: FloatLabelType;
	/** to check if dob is invalid - to throw error in dob field */
	@Input() invalidDateOfBirth: boolean;
	/** input field to clear dob if enabled */
	@Input() clearDOB: boolean;
	/** dob form group */
	dobForm: FormGroup;
	/** holds days field - 1 to 30 or 31 or 28 or 29 */
	days: any = [];
	/** holds array of month values */
	months: any = [
		{ id: 1, value: 'January', days: 31 },
		{ id: 2, value: 'February', days: 29 },
		{ id: 3, value: 'March', days: 31 },
		{ id: 4, value: 'April', days: 30 },
		{ id: 5, value: 'May', days: 31 },
		{ id: 6, value: 'June', days: 30 },
		{ id: 7, value: 'July', days: 31 },
		{ id: 8, value: 'August', days: 31 },
		{ id: 9, value: 'September', days: 30 },
		{ id: 10, value: 'October', days: 31 },
		{ id: 11, value: 'November', days: 30 },
		{ id: 12, value: 'December', days: 31 },
	];
	/** holds array of years value */
	years: any = [];
	/** Placeholder for month dropdown */
	placeHolderMonth = 'Month';
	/** placeholder for day dropdown */
	placeHolderDay = 'Day';
	/** placeholder for year dropdown */
	placeHolderYear = 'Year';
	/** Removes any year under 18 the from current year */
	@Input() removeEighteenYears: boolean = false;
	/** If the age submitted has been deemed invalid by an external validator */
	@Input() invalidAge: boolean = false;
	@Input() sqlDateFormat: boolean = false;

	// Subject to clean up the subscriptions with takeUntil and lifecyles
	private unsubscribe$ = new Subject<void>();

	@HostListener('window:resize') onResize() {
		this.updateIsMobileView();
	}

	updateIsMobileView(): void {
		if (window.innerWidth < 800) {
			this.isMobileView = true;
		} else {
			this.isMobileView = false;
		}
	}
	
	/**
	 * DOB Component constructor
	 */
	constructor() {
		this.updateIsMobileView();
	}

	/**
	 * On component initialization
	 */
	ngOnInit() {
		this.control = this.control ?? 'dateOfBirth';
		this.floatLabel = this.floatLabel ?? 'auto';
		this.invalidDateOfBirth = false;
		this.clearDOB = false;
		this.initializeDobForm();
		this.dobForm
			.get('month')
			.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe(value => {
				if (value) {
					this.placeHolderMonth = '';
					const month = this.months.filter(object => {
						return object.id === value;
					});
					if (month) {
						let day = 1;
						this.days = [];
						for (let i = 0; i < month[0].days; i++) {
							this.days.push(day++);
						}
					}
					this.checkIfFutureDate();
					this.submitForm();
				}
			});

		this.dobForm
			.get('day')
			.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe(value => {
				if (value) {
					this.placeHolderDay = '';
					this.checkIfFutureDate();
					this.submitForm();
				}
			});

		this.dobForm
			.get('year')
			.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe(value => {
				if (value) {
					this.placeHolderYear = '';
					if (value % 4 !== 0 && this.dobForm.get('day').value === 29 && this.dobForm.get('month').value === 2) {
						this.dobForm.get('day').setValue('');
						let dayCount = 1;
						this.days = [];
						for (let i = 0; i < 28; i++) {
							this.days.push(dayCount++);
						}
						this.dobForm.get('day').setErrors({ invalidDay: true });
					} else {
						if (this.dobForm.get('day').value) {
							this.dobForm.get('day').setErrors(null);
						}
					}
					this.checkIfFutureDate();
					this.submitForm();
				}
			});
	}

	/**
	 * DOB component initialization
	 */
	initializeDobForm() {
		this.initializeDayAndMonth();
		this.dobForm = new FormGroup({
			month: new FormControl('', Validators.compose([Validators.required])),
			day: new FormControl('', Validators.compose([Validators.required])),
			year: new FormControl('', Validators.compose([Validators.required])),
		});
	}

	/**
	 * On component changes
	 */
	ngOnChanges() {
		if (this.invalidDateOfBirth) {
			this.dobForm.markAsTouched();
		}
		if (this.clearDOB) {
			this.initializeDobForm();
			this.dobForm.markAsTouched();
			this.formGroup.get(this.control).setValue('');
			this.placeHolderMonth = 'Month';
			this.placeHolderDay = 'Day';
			this.placeHolderYear = 'Year';
		}
		this.setErrorsForInvalidAge();
	}

	/**
	 * Check if date is future date - if so error is thrown in UI
	 */
	checkIfFutureDate() {
		const day: AbstractControl<number> = this.dobForm.get('day');
		const month: AbstractControl<number> = this.dobForm.get('month');
		const year: AbstractControl<number> = this.dobForm.get('year');
		if (year.value === DateUtil.getCurrentYear()) {
			const currentDate = DateUtil.getCurrentDate();
			if (month.value - 1 > moment(currentDate).month()) {
				month.setErrors({ invalidMonth: true });
				day.setErrors({ invalidDay: true });
			} else if (month.value - 1 === moment(currentDate).month() && day.value > moment(currentDate).date()) {
				day.setErrors({ invalidDay: true });
			} else {
				this.clearErrors(day, month, year);
			}
		} else {
			this.clearErrors(day, month, year);
		}
	}

	/**
	 * Set errors based on the invalidAge flag if other custom errors are not set
	 *
	 * @memberof DobComponent
	 */
	setErrorsForInvalidAge() {
		const day: AbstractControl<number> = this.dobForm?.get('day');
		const month: AbstractControl<number> = this.dobForm?.get('month');
		const year: AbstractControl<number> = this.dobForm?.get('year');

		if (month?.getError('invalidMonth') || day?.getError('invalidDay')) {
			return;
		}

		if (this.invalidAge) {
			day.setErrors({});
			month.setErrors({});
			year.setErrors({});
		} else {
			this.clearErrors(day, month, year);
		}
	}

	/**
	 * Clears the errors of the day, month and year control if the value is present.
	 *
	 * @param {*} day
	 * @param {*} month
	 * @param {*} year
	 * @memberof DobComponent
	 */
	clearErrors(day: AbstractControl<number>, month: AbstractControl<number>, year: AbstractControl<number>) {
		if (month?.value) {
			month.setErrors(null);
		}
		if (day?.value) {
			day.setErrors(null);
		}
		if (year?.value) {
			year.setErrors(null);
		}
	}

	/**
	 * To initialize date and month values
	 */
	initializeDayAndMonth() {
		let currentYear = +DateUtil.getCurrentYear();
		if (this.removeEighteenYears) {
			currentYear -= 18;
		}
		for (let i = 0; i < 100; i++) {
			this.years.push(currentYear--);
		}
		let day = 1;
		for (let i = 0; i < 30; i++) {
			this.days.push(day++);
		}
	}

	/**
	 * Submits dob form when form is valid
	 */
	submitForm() {
		if (this.dobForm.valid) {
			const dobInfo = this.dobForm.getRawValue();
			const date = `${dobInfo.month}/${dobInfo.day}/${dobInfo.year}`;
			this.formGroup.get(this.control).markAsTouched();
			if (this.sqlDateFormat) {
				this.formGroup.get(this.control).setValue(DateUtil.getDateInSqlFormat(date));
			} else {
				this.formGroup.get(this.control).setValue(DateUtil.getDateInFormatMMDDYYYY(date));
			}
		} else {
			this.formGroup.get(this.control).setValue('');
			this.formGroup.get(this.control).markAsUntouched();
		}

		this.setErrorsForInvalidAge();
	}

	/**
	 * On component destruction
	 */
	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}
}
